import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import BackButton from '../../components/ui/BackButton';
import IncidentForm from './components/IncidentForm';
import IncidentList from './components/IncidentList';
import IncidentDeleteConfirmModal from './components/IncidentDeleteConfirmModal';
import { incidentService } from '../../services/incidentService';
import { siteService } from '../../services/siteService';
import { companyService } from '../../services/companyService';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import { pdfExportService } from '../../services/pdfExportService';
import { useLanguage } from '../../contexts/LanguageContext';
import useSubscriptionReadOnly from '../../hooks/useSubscriptionReadOnly';
import SubscriptionReadOnlyBanner from '../../components/SubscriptionReadOnlyBanner';
import useSiteReadOnly from '../../hooks/useSiteReadOnly';
import SiteReadOnlyBanner from '../../components/SiteReadOnlyBanner';
import { toast } from 'sonner';

const IncidentReporting = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const siteIdParam = searchParams?.get('siteId');
  const { userProfile } = useAuth();
  const { t } = useLanguage();
  const { isReadOnly: subscriptionReadOnly } = useSubscriptionReadOnly();

  const [activeView, setActiveView] = useState('form');
  const [loading, setLoading] = useState(false);
  const [sites, setSites] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [companyId, setCompanyId] = useState(null);
  const [selectedSiteId, setSelectedSiteId] = useState(siteIdParam || '');
  const { status: siteStatus, isReadOnly: siteIsReadOnly } = useSiteReadOnly(selectedSiteId || null);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [incidentsLoadError, setIncidentsLoadError] = useState('');

  const isWorker = userProfile?.role === 'worker';
  // NSherjFh PR 5: Delete + Restore + the 'View deleted' toggle are
  // admin-only. Site managers / workers retain Report + status update.
  const isAdmin = userProfile?.role === 'admin';
  const [viewDeleted, setViewDeleted] = useState(false);
  const [deletingIncident, setDeletingIncident] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const homePath = isWorker ? '/worker-today' : userProfile?.role === 'site_manager' ? '/site-manager-dashboard' : '/admin-dashboard';

  const breadcrumbItems = [
    { label: t('home'), href: homePath },
    { label: t('incidentReporting') }
  ];

  useEffect(() => {
    loadInitialData();
  }, [userProfile]);

  useEffect(() => {
    if (selectedSiteId) {
      loadIncidents(selectedSiteId);
    }
  }, [selectedSiteId, viewDeleted]);

  const loadInitialData = async () => {
    try {
      setLoading(true);

      // Use getUserCompany() — works for all roles (admin, site_manager, worker)
      const companyResult = await companyService?.getUserCompany();
      if (!companyResult?.data?.id) {
        // No company found — nothing to load
        setLoading(false);
        return;
      }

      setCompanyId(companyResult?.data?.id);

      let siteList = [];

      if (isWorker) {
        // Workers: scope to their assigned sites only
        const workerSites = await siteService?.getSitesByUser();
        siteList = workerSites?.data || [];

        // Fallback: company sites if no direct assignment found
        if (!siteList?.length) {
          const companySites = await siteService?.getSitesByCompany(companyResult?.data?.id);
          siteList = companySites?.data || [];
        }
      } else {
        // Admin / site_manager: company-scoped sites
        const sitesResult = await siteService?.getSitesByCompany(companyResult?.data?.id);
        siteList = sitesResult?.data || [];
      }

      setSites(siteList);

      if (siteIdParam && siteList?.some(s => s?.id === siteIdParam)) {
        setSelectedSiteId(siteIdParam);
      } else if (!selectedSiteId && siteList?.length > 0) {
        setSelectedSiteId(siteList?.[0]?.id);
      }
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadIncidents = async (siteId) => {
    setIncidentsLoadError('');
    try {
      const result = await incidentService?.getIncidentsBySite(siteId, { includeDeleted: viewDeleted });
      const rows = result?.data || [];
      // NSherjFh PR 5: deleted tab shows ONLY soft-deleted incidents.
      // includeDeleted: true returns BOTH active and deleted; narrow
      // to the deleted ones for display. Active tab is pre-filtered
      // by the service default.
      setIncidents(viewDeleted ? rows?.filter((i) => i?.deleted_at) : rows);
    } catch (err) {
      console.error('Error loading incidents:', err);
      setIncidentsLoadError('Failed to load incidents for this site. Please refresh.');
    }
  };

  // NSherjFh PR 5 — soft-delete handlers. Mirrors PRs 3/4 flow.
  const handleDeleteClick = (incident) => {
    setDeletingIncident(incident);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingIncident?.id) {
      setIsDeleteModalOpen(false);
      return;
    }
    const target = deletingIncident;
    try {
      const result = await incidentService?.deleteIncident(target?.id);
      if (result?.error) throw new Error(result?.error?.message || 'Delete failed');
      setIncidents((prev) => prev?.filter((i) => i?.id !== target?.id));
      toast?.success('Incident moved to Deleted');
    } catch (err) {
      console.error('[IncidentReporting] delete failed:', err);
      toast?.error(err?.message || 'Failed to delete incident');
    } finally {
      setIsDeleteModalOpen(false);
      setDeletingIncident(null);
    }
  };

  const handleRestore = async (incident) => {
    try {
      const result = await incidentService?.restoreIncident(incident?.id);
      if (result?.error) throw new Error(result?.error?.message || 'Restore failed');
      setIncidents((prev) => prev?.filter((i) => i?.id !== incident?.id));
      toast?.success('Incident restored');
    } catch (err) {
      console.error('[IncidentReporting] restore failed:', err);
      toast?.error(err?.message || 'Failed to restore incident');
    }
  };

  const handleIncidentSubmit = async (incidentData) => {
    if (siteIsReadOnly) {
      toast?.error('This site is completed — please reactivate to continue.');
      return;
    }
    const result = await incidentService?.createIncident({
      ...incidentData,
      companyId,
      siteId: selectedSiteId
    });

    if (result?.data) {
      setActiveView('list');
      loadIncidents(selectedSiteId);
      return { success: true };
    }
    return { success: false, error: result?.error?.message };
  };

  const handleExportPdf = async () => {
    try {
      setExportingPdf(true);
      const site = sites?.find(s => s?.id === selectedSiteId);
      await pdfExportService?.generateIncidentReport({
        incidents,
        siteName: site?.site_name || '',
        companyName: '',
        dateFrom: null,
        dateTo: null,
      });
    } catch (err) {
      console.error('PDF export error:', err);
      toast?.error('Failed to export PDF. Please try again.');
    } finally {
      setExportingPdf(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Incident Reporting - SiteProof</title>
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-20 pb-8 px-3 sm:px-4 lg:px-6 max-w-7xl mx-auto">
          <div className="mb-6 flex items-center gap-4">
            <BackButton fallback={homePath} />
            <Breadcrumb items={breadcrumbItems} />
          </div>

          <SubscriptionReadOnlyBanner className="mb-6" />
          <SiteReadOnlyBanner status={siteStatus} siteId={selectedSiteId} className="mb-6" />

          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between mb-6 gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-foreground mb-2">
                {viewDeleted ? 'Deleted incidents' : t('incidentReporting')}
              </h1>
              <p className="text-sm text-muted-foreground">
                {viewDeleted
                  ? 'Incident reports removed from active views. Restore to bring them back.'
                  : t('incidentReportingSubtitle')}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {/* NSherjFh PR 5: admin-only View Deleted toggle. Toggling
                  forces activeView=list (the Report form has nothing to
                  do with deleted reports). */}
              {isAdmin && (
                <Button
                  variant={viewDeleted ? 'default' : 'outline'}
                  onClick={() => { setActiveView('list'); setViewDeleted((v) => !v); }}
                  iconName={viewDeleted ? 'FolderOpen' : 'Trash2'}
                  className="flex-1 sm:flex-none min-h-[48px]"
                >
                  {viewDeleted ? 'View active' : 'View deleted'}
                </Button>
              )}
              {!viewDeleted && (
                <Button
                  variant={activeView === 'form' ? 'default' : 'outline'}
                  onClick={() => setActiveView('form')}
                  iconName="Plus"
                  className="flex-1 sm:flex-none min-h-[48px]"
                  disabled={subscriptionReadOnly || siteIsReadOnly}
                >
                  {t('reportIncident')}
                </Button>
              )}
              {!viewDeleted && (
                <Button
                  variant={activeView === 'list' ? 'default' : 'outline'}
                  onClick={() => setActiveView('list')}
                  iconName="List"
                  className="flex-1 sm:flex-none min-h-[48px]"
                >
                  {t('viewReports')}
                </Button>
              )}
              {!viewDeleted && activeView === 'list' && incidents?.length > 0 && (
                <Button
                  variant="outline"
                  onClick={handleExportPdf}
                  iconName="Download"
                  disabled={exportingPdf}
                  className="w-full sm:w-auto min-h-[48px]"
                >
                  {exportingPdf ? t('exporting') : t('exportPDF')}
                </Button>
              )}
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
            </div>
          ) : (
            <>
              {!viewDeleted && activeView === 'form' && (
                <IncidentForm
                  sites={sites}
                  selectedSiteId={selectedSiteId}
                  onSiteChange={setSelectedSiteId}
                  onSubmit={handleIncidentSubmit}
                  companyId={companyId}
                />
              )}

              {(viewDeleted || activeView === 'list') && (
                <>
                  {incidentsLoadError && (
                    <div className="mb-4 flex items-start gap-3 bg-destructive/10 border border-destructive/20 text-destructive rounded-lg px-4 py-3">
                      <p className="text-sm font-medium">{incidentsLoadError}</p>
                    </div>
                  )}
                  <IncidentList
                    incidents={incidents}
                    sites={sites}
                    selectedSiteId={selectedSiteId}
                    onSiteChange={setSelectedSiteId}
                    onDelete={!viewDeleted && isAdmin ? handleDeleteClick : null}
                    onRestore={viewDeleted && isAdmin ? handleRestore : null}
                  />
                </>
              )}
            </>
          )}
        </main>
        <IncidentDeleteConfirmModal
          isOpen={isDeleteModalOpen}
          onClose={() => { setIsDeleteModalOpen(false); setDeletingIncident(null); }}
          onConfirm={handleConfirmDelete}
          incidentLabel={deletingIncident?.location_description || deletingIncident?.incident_type || ''}
        />
      </div>
    </>
  );
};

export default IncidentReporting;